import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing_session");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      while (!cancelled && attempts < 12) {
        attempts += 1;
        try {
          const r = await api.billingCheckoutStatus(sessionId);
          if (
            r?.payment_status === "paid" ||
            r?.plan === "pro" ||
            r?.subscription_status === "active"
          ) {
            const fresh = await api.billingPlan().catch(() => null);
            if (!cancelled) {
              setPlan(fresh);
              setStatus("success");
            }
            return;
          }
          if (r?.status === "expired") {
            if (!cancelled) setStatus("expired");
            return;
          }
        } catch (_) {
          /* ignore — poll again */
        }
        await new Promise((res) => setTimeout(res, 2500));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen cre-grid-bg flex flex-col">
      <Header />
      <div className="flex-1 max-w-3xl mx-auto w-full px-6 md:px-8 py-16">
        <div
          data-testid="checkout-success-page"
          className="cre-surface rounded-sm p-8 md:p-10 text-center"
        >
          {status === "polling" ? (
            <>
              <Loader2 className="h-10 w-10 mx-auto text-violet-600 animate-spin" />
              <h1 className="mt-4 font-heading text-2xl font-bold">
                Confirming your payment…
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Stripe is wrapping up your subscription. This usually takes a
                couple of seconds.
              </p>
            </>
          ) : null}

          {status === "success" ? (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <h1
                data-testid="checkout-success-title"
                className="mt-4 font-heading text-3xl font-bold"
              >
                Welcome to CRE Pro
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Your account now has{" "}
                <span className="font-mono font-semibold">
                  {plan?.monthly_analysis_limit ?? 30}
                </span>{" "}
                CRE reasoning runs per month, with stakeholder-ready RCA
                summaries, CRE Score, reasoning trail, priority incident
                history, and role-wise views for Incident Manager, Support/SRE,
                Leadership, and Management.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-violet-500/10 border border-violet-500/30 text-violet-700 text-xs font-mono">
                <Sparkles className="h-3.5 w-3.5" />
                Pro · {plan?.currency} {plan?.plan_price?.toFixed(2)}/month
              </div>
              <div className="mt-6 flex justify-center gap-2">
                <Button
                  onClick={() => navigate("/dashboard")}
                  data-testid="checkout-success-dashboard-btn"
                  className="cre-cta-primary text-white"
                >
                  Back to dashboard
                </Button>
                <Link to="/incidents/new">
                  <Button variant="outline">Raise new incident</Button>
                </Link>
              </div>
            </>
          ) : null}

          {(status === "timeout" || status === "expired" || status === "missing_session") ? (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-amber-600" />
              <h1 className="mt-4 font-heading text-2xl font-bold">
                We couldn't confirm the payment yet
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Stripe will email you a receipt once the subscription is
                active. If your plan doesn't update on the dashboard within a
                minute, please contact support.
              </p>
              <div className="mt-6">
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  data-testid="checkout-success-fallback-btn"
                >
                  Back to dashboard
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  );
}
